import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, ButtonGroup, Heading, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing } from '@zg-rentals/rental-manager-env';

import { useQueryClient } from '@zg-rentals/react-query5';

import { useSelector } from '../../../selectors/useSelector';

import LeadActions from '../../../actions/LeadActions';
import PropertiesActions from '../../../actions/PropertiesActions';

import { DEACTIVATION_REASONS, STEPS } from './constants';
import DispatchConstants from '../../../constants/DispatchConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import TenantDirectoryCSEvents from '../../../model/ClickstreamEvent/TenantDirectoryCSEvents';

const { ADD_TENANT_STEP_TRIGGER_SOURCE, LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE } = TenantDirectoryCSEvents;

import propertyUtils from '../../../utils/propertyUtils';

import { useGhostedLeads, useNotAvailableMessage } from '../DeactivationModal/hooks';

import AddTenantSelectStep from './components/AddTenantSelectStep';
import DeactivationReasonStep from './components/DeactivationReasonStep';
import NotifyLeads from '../DeactivationModal/components/NotifyLeads';
import UpsellStep from './components/UpsellStep';

import type { DeactivationReason, Step } from './constants';
import type { RentalManagerDeactivateListingReason } from '@zg-rentals/rental-manager-env';
import type { TenantSelectFormBodyRef } from '@zg-rentals/modal-tenant';

const { SYNDICATION_OVERVIEW_STATUS, LISTING_STATUS } = PropertyConstants;

type DeactivateListingModalProps = {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
};

const ERROR_MESSAGES = {
  DEFAULT: 'There was an error. Please try again',
};

const DeactivateListingModal = ({ open = false, onOpenChange }: DeactivateListingModalProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  /**
   * NOTE: needed to handle `/:propertyId/listing-availability`
   */
  const { propertyId } = useParams() as { propertyId: string };

  const [step, setStep] = useState<Step>(STEPS.DEACTIVATION_REASON);
  const [deactivationReason, setDeactivationReason] = useState<DeactivationReason | null>(null);
  const [isAddTenantFormValid, setIsAddTenantFormValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantSelectFormBodyRef = useRef<TenantSelectFormBodyRef>(null);

  const {
    currentProperty,
    listingAlias: currentPropertyListingAlias,
    isMultifamily,
    syndication,
    street,
    contactName,
    user,
  } = useSelector(
    (state) => ({
      contactName: state?.properties?.currentProperty?.contactName,
      currentProperty: state?.properties?.currentProperty,
      isMultifamily: state?.properties?.currentProperty?.isMultifamily,
      listingAlias: state?.properties?.currentProperty?.listingAlias,
      street: state?.properties?.currentProperty?.street,
      syndication: state?.properties?.currentProperty?.syndication,
      user: state?.user,
    }),
    shallowEqual,
  );

  const listingAlias = useMemo(() => {
    return currentPropertyListingAlias || propertyId;
  }, [propertyId, currentPropertyListingAlias]);

  const { ghostedLeads, toggleIncludeLead } = useGhostedLeads(listingAlias, isMultifamily);
  const { notAvailableMessage, setNotAvailableMessage } = useNotAvailableMessage(street, contactName);

  const { mutate: deactivateListing, isPending } =
    useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing();

  const getIsPrimaryCTADisabled = (): boolean => {
    if (isPending) return true;

    switch (step) {
      case STEPS.DEACTIVATION_REASON: {
        return deactivationReason === null;
      }
      case STEPS.ADD_TENANT_SELECT: {
        return !isAddTenantFormValid;
      }
      case STEPS.NOTIFY_LEADS: {
        const noLeadsSelected = ghostedLeads.filter(({ isIncluded }) => isIncluded).length === 0;

        return noLeadsSelected;
      }
      default: {
        return true;
      }
    }
  };

  const getModalConfig = (): { heading: string; primaryButtonLabel: string; secondaryButtonLabel: string } => {
    switch (step) {
      case STEPS.ADD_TENANT_SELECT:
        return { heading: 'Your tenants', primaryButtonLabel: 'Save & continue', secondaryButtonLabel: 'Skip' };
      case STEPS.NOTIFY_LEADS:
        return {
          heading: "Notify renters who you haven't responded to yet?",
          primaryButtonLabel: 'Send and continue',
          secondaryButtonLabel: 'Skip',
        };
      case STEPS.UPSELL:
        return {
          heading: 'Next steps to make this move easier with Zillow',
          primaryButtonLabel: '',
          secondaryButtonLabel: '',
        };
      case STEPS.DEACTIVATION_REASON:
      default:
        return {
          heading: 'Why are you deactivating this listing?',
          primaryButtonLabel: 'Deactivate listing',
          secondaryButtonLabel: 'Cancel',
        };
    }
  };

  const modalConfig = getModalConfig();
  const isPrimaryCTADisabled = getIsPrimaryCTADisabled();
  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  useEffect(() => {
    if (open) {
      return () => {
        resetState();
      };
    }
  }, [open]);

  const closeModal = ({
    triggerAddTenantStepCloseEvent = false,
    triggerUpsellCloseEvent = false,
  }: { triggerAddTenantStepCloseEvent?: boolean; triggerUpsellCloseEvent?: boolean } = {}) => {
    if (triggerAddTenantStepCloseEvent) {
      triggerCSAddTenantStepClick(ADD_TENANT_STEP_TRIGGER_SOURCE.CLOSE);
    }

    if (triggerUpsellCloseEvent) {
      triggerCSLeaseAndPaymentUpsellStepClick(LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE.CLOSE);
    }

    onOpenChange(false);
  };

  const getShouldNotifyLeads = (): boolean => {
    return deactivationReason !== DEACTIVATION_REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW && ghostedLeads.length > 0;
  };

  const resetState = () => {
    setError(null);
    setIsAddTenantFormValid(false);
    setDeactivationReason(null);
    setStep(STEPS.DEACTIVATION_REASON);
  };

  const updateCurrentProperty = () => {
    const dataToUpdate = {
      active: false,
      expirationDateTimestamp: Date.now(),
      status: 'OFF-MARKET',
      syndication: {
        ...syndication,
        overview: {
          status: SYNDICATION_OVERVIEW_STATUS.INACTIVE,
          displayStatus: LISTING_STATUS.INACTIVE,
          publishedCount: 0,
        },
      },
    };
    dispatch(PropertiesActions.updateCurrentProperty(dataToUpdate));

    dispatch({
      type: DispatchConstants.UPDATE_PROPERTY_OVERVIEW,
      payload: dataToUpdate,
    });
  };

  const invalidatePropertyQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
    queryClient.invalidateQueries({ queryKey: ['getListingSummary', listingAlias] });
    queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
  };

  const navigateAfterNotifyLeadsStep = () => {
    if (isRoomForRent === false) {
      setStep(STEPS.UPSELL);
    } else {
      closeModal();

      window.router.transitionTo(`/rental-manager/properties/${listingAlias}/dashboard/overview`);
    }
  };

  const navigateAfterTenantSelectStep = () => {
    const shouldNotifyLeads = getShouldNotifyLeads();

    if (shouldNotifyLeads) {
      setStep(STEPS.NOTIFY_LEADS);
    } else {
      navigateAfterNotifyLeadsStep();
    }
  };

  const triggerCSLeaseAndPaymentUpsellStepClick = (
    triggerSource: (typeof TenantDirectoryCSEvents.LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE)[keyof typeof TenantDirectoryCSEvents.LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE],
  ) => {
    if (!currentProperty || !user) return;

    TenantDirectoryCSEvents.LeaseAndPaymentUpsellStepClick({
      user,
      currentProperty,
      triggerSource,
    });
  };

  const triggerCSAddTenantStepClick = (
    triggerSource: (typeof TenantDirectoryCSEvents.ADD_TENANT_STEP_TRIGGER_SOURCE)[keyof typeof TenantDirectoryCSEvents.ADD_TENANT_STEP_TRIGGER_SOURCE],
  ) => {
    if (!currentProperty || !user) return;

    TenantDirectoryCSEvents.AddTenantStepClick({
      user,
      currentProperty,
      triggerSource,
    });
  };

  const triggerCSDeactivationReasonClick = (answer: DeactivationReason) => {
    if (!currentProperty || !user) return;

    const triggerObjectMap: Record<DeactivationReason, 'found_new_tenant' | 'tenant_renewed' | 'still_looking'> = {
      [DEACTIVATION_REASONS.FOUND_RENTER_ELSEWHERE]: 'found_new_tenant',
      [DEACTIVATION_REASONS.TENANT_RENEWED]: 'tenant_renewed',
      [DEACTIVATION_REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW]: 'still_looking',
    };

    const value = triggerObjectMap[answer];

    if (value) {
      TenantDirectoryCSEvents.DeactivationReasonClick({
        user,
        currentProperty,
        triggerObject: value,
      });
    }
  };

  const handleAnswerCardClick = (answer: DeactivationReason | null) => {
    setDeactivationReason(answer);

    if (answer) {
      triggerCSDeactivationReasonClick(answer);
    }
  };

  const handleSecondaryCTA = () => {
    switch (step) {
      case STEPS.DEACTIVATION_REASON: {
        closeModal();
        break;
      }
      case STEPS.ADD_TENANT_SELECT: {
        triggerCSAddTenantStepClick(ADD_TENANT_STEP_TRIGGER_SOURCE.SKIP);
        navigateAfterTenantSelectStep();
        break;
      }
      case STEPS.NOTIFY_LEADS: {
        navigateAfterNotifyLeadsStep();
        break;
      }
      default: {
        closeModal();
      }
    }
  };

  const handleDeactivationReasonStep = () => {
    if (!deactivationReason) return;

    deactivateListing(
      {
        postData: {
          listingAlias,
          reason: deactivationReason as RentalManagerDeactivateListingReason,
        },
      },
      {
        onSuccess: () => {
          enqueueToast(<Toast tone="success">Listing deactivated successfully</Toast>);

          setError(null);
          updateCurrentProperty();
          invalidatePropertyQueries();

          if (deactivationReason === DEACTIVATION_REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW) {
            closeModal();

            return;
          }

          setStep(STEPS.ADD_TENANT_SELECT);
        },
        onError: () => {
          setError(ERROR_MESSAGES.DEFAULT);
        },
      },
    );
  };

  const handleAddTenantSelectStep = async () => {
    triggerCSAddTenantStepClick(ADD_TENANT_STEP_TRIGGER_SOURCE.SAVE_AND_CONTINUE);

    if (tenantSelectFormBodyRef.current) {
      // error handling is self contained within the TenantSelectFormBody component
      await tenantSelectFormBodyRef.current.addRenters();
    }
  };

  const handleNotifyLeads = async () => {
    const requests = ghostedLeads.map(({ conversationId, isIncluded }) => {
      if (isIncluded) {
        return dispatch(LeadActions.addMessage({ conversationId, message: notAvailableMessage }));
      }
      return Promise.resolve(true);
    });

    return Promise.all(requests).catch(() => setError(ERROR_MESSAGES.DEFAULT));
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal({
        triggerAddTenantStepCloseEvent: step === STEPS.ADD_TENANT_SELECT,
        triggerUpsellCloseEvent: step === STEPS.UPSELL,
      });
      return;
    }

    onOpenChange(isOpen);
  };

  const handlePrimaryCTA = async () => {
    // reset error
    setError(null);

    switch (step) {
      case STEPS.DEACTIVATION_REASON: {
        handleDeactivationReasonStep();
        break;
      }
      case STEPS.ADD_TENANT_SELECT: {
        handleAddTenantSelectStep();
        break;
      }
      case STEPS.NOTIFY_LEADS: {
        await handleNotifyLeads();

        navigateAfterNotifyLeadsStep();
        break;
      }
      default: {
        break;
      }
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case STEPS.DEACTIVATION_REASON:
        return (
          <DeactivationReasonStep deactivationReason={deactivationReason} onAnswerSelect={handleAnswerCardClick} />
        );
      case STEPS.ADD_TENANT_SELECT:
        return (
          <AddTenantSelectStep
            deactivationReason={deactivationReason}
            ref={tenantSelectFormBodyRef}
            onValidationChange={setIsAddTenantFormValid}
            onSubmitSuccess={() => {
              navigateAfterTenantSelectStep();
            }}
          />
        );
      case STEPS.NOTIFY_LEADS:
        return (
          <NotifyLeads
            hideHeading={true}
            leads={ghostedLeads}
            message={notAvailableMessage}
            setMessage={setNotAvailableMessage}
            toggleIncludeLead={toggleIncludeLead}
          />
        );
      case STEPS.UPSELL:
        return (
          <UpsellStep
            listingAlias={listingAlias}
            onCTAClick={closeModal}
            onClickstreamEvent={triggerCSLeaseAndPaymentUpsellStepClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      data-testid="DeactivateListingModal"
      data-eventid={
        step === STEPS.ADD_TENANT_SELECT
          ? TenantDirectoryCSEvents.AddTenantStepClick.data_event_id
          : step === STEPS.UPSELL
            ? TenantDirectoryCSEvents.LeaseAndPaymentUpsellStepClick.data_event_id
            : undefined
      }
      open={open}
      onOpenChange={handleOpenChange}
      header={
        <Heading level={2} textStyle="responsive.heading-xs" css={{ textAlign: 'left' }}>
          {modalConfig.heading}
        </Heading>
      }
      body={
        <>
          {error && (
            <Box css={{ marginBottom: '200' }}>
              <Alert tone="warning">{error}</Alert>
            </Box>
          )}

          {renderStepContent()}
        </>
      }
      footer={
        step !== STEPS.UPSELL ? (
          <ButtonGroup aria-label="deactivation actions">
            <TextButton
              data-testid="CancelButton"
              css={{ paddingLeft: '400', paddingRight: '400' }}
              onClick={handleSecondaryCTA}
              disabled={isPending}
            >
              {modalConfig.secondaryButtonLabel}
            </TextButton>

            <Button
              data-testid="PrimaryCTAButton"
              emphasis="filled"
              tone="brand"
              onClick={handlePrimaryCTA}
              disabled={isPrimaryCTADisabled}
              loading={isPending}
            >
              {modalConfig.primaryButtonLabel}
            </Button>
          </ButtonGroup>
        ) : undefined
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default DeactivateListingModal;
